import { Input, EventEmitter, Output, OnChanges, OnDestroy, SimpleChanges, ElementRef, Directive } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Course, Learnspace, LearnspaceMode } from '@shared/models';
import { Hub } from '@shared/models/hub.model';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { CurrentHubService } from '@shared/services/currentHub.service';
import { PaginatedScrollService, PaginatedScrollController } from '@shared/services/paginated-scroll.service';

@Directive()
export abstract class CoursesListBaseComponent implements OnChanges, OnDestroy {
  @Input() learnspaceCourse: Learnspace;
  @Input() canCreateCourse: boolean;
  @Input() canEdit: boolean = true;
  @Output() onListChanged = new EventEmitter<any>();

  @Input() searchTerm: string;
  courses: Course[] = [];
  hubObservable: Observable<Hub>;

  protected _paginationController: PaginatedScrollController<Course>;

  constructor(
    protected _courseApiService: CoursesAPIService,
    protected _currentHubService: CurrentHubService,
    protected _elRef: ElementRef,
    protected _paginatedScrollService: PaginatedScrollService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['learnspaceCourse'] && this.learnspaceCourse?.id) {
      this.loadCourses();
    }
    if (changes['searchTerm'] && !changes['searchTerm'].firstChange) {
      const search = this.searchTerm ? `title:%${this.searchTerm}%` : undefined;
      this._paginationController?.reload({ search }).subscribe();
    }
  }

  ngOnDestroy(): void {
    this._paginationController?.destroy();
  }

  loadCourses(): void {
    this._paginationController?.destroy();
    const sort = this.learnspaceCourse.mode === LearnspaceMode.program ? 'position' : 'status,-createdAt';
    this._paginationController = this._paginatedScrollService.createController(
      options => this._courseApiService.getLearnspaceCourseList(this.learnspaceCourse.id, options)
        .pipe(map(({ total, courses }) => ({ total, items: courses }))),
      items => this.courses = items,
      { pageSize: 10, sort }
    );
    this._paginationController.reload().subscribe({ next: () => this._paginationController.attachScroll(this.getScrollContainer()) });
  }

  private getScrollContainer(): Element {
    let el = this._elRef.nativeElement.parentElement;
    while (el) {
      const overflowY = window.getComputedStyle(el).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') return el;
      el = el.parentElement;
    }
    return document.documentElement;
  }

  onSearch(): void {
    const search = this.searchTerm ? `title:%${this.searchTerm}%` : undefined;
    this._paginationController?.reload({ search }).subscribe();
  }

  onListChange(event: { action: string, content: Course | string }): void {
    this.onListChanged.emit(event);
    this.loadCourses();
  }
}
